import { match } from "ts-pattern";
import { computed, ref, watch, type ComputedRef, type Ref } from "vue";

import { useBrowserStore } from "@/composables/useBrowserStore";
import type { UseProfile } from "@/composables/useProfile";
import type { ConsoleAction } from "@/types";
import type { Billing } from "@/utils/billing";

// Contextual capture gating. On macOS the console always shows a capture
// affordance: a permission-free preview (frontmost app name + icon) when the
// plugin is off, the user isn't entitled, or Accessibility isn't granted
// yet, and a deep capture (tab URL, selection, …) once the plugin is enabled
// and allowed. Performing a gated capture opens the upgrade or plugin modal
// instead of creating anything.

export const CONTEXTUAL_CAPTURE_ENTITLEMENT = "contextual-capture";
export const CONTEXTUAL_CAPTURE_PLUGIN_ID = "contextual-capture";
export const CONTEXTUAL_CAPTURE_FEATURE_NAME = "Contextual Capture";

export type CaptureAvailability = {
  /** The user's plan includes contextual capture. */
  entitled: boolean;
  /** The Contextual Capture plugin is enabled (Settings → Plugins). */
  enabled: boolean;
};

/**
 * Recompute the capture availability from the app-wide `billing` and
 * `isContextualCaptureEnabled` injections (pass them in from a `setup`
 * function). Both windows hosting a console provide them: `Routine.vue` in the
 * main window and the dashboard window's `App.vue`.
 */
export const useCaptureAvailability = (
  billing: Ref<Billing | undefined>,
  enabled: Ref<boolean>
): ComputedRef<CaptureAvailability> => {
  const entitlement = computed(() =>
    billing.value?.isEntitledTo(CONTEXTUAL_CAPTURE_ENTITLEMENT)
  );
  return computed(() => ({
    entitled: entitlement.value?.value ?? false,
    enabled: enabled.value,
  }));
};

/** Why a capture action can't be performed, or null when it can. */
export type CaptureGate = "upgrade" | "plugin";

/** Whether the action stores the captured context (task url, custom-type
 * context property, or a backend capture variant). */
export const isCaptureAction = (action: ConsoleAction): boolean =>
  match(action)
    .with({ type: "custom-type" }, ({ context }) => context)
    .with(
      { type: "task" },
      { type: "task-allocation" },
      { type: "task-postponing" },
      { type: "task-scheduling" },
      ({ capture }) => !!capture
    )
    .otherwise(() => false);

export const captureGate = (
  action: ConsoleAction,
  availability: CaptureAvailability
): CaptureGate | null => {
  if (!isCaptureAction(action)) return null;
  if (!availability.entitled) return "upgrade";
  if (!availability.enabled) return "plugin";
  return null;
};

// Gated capture actions double as an advertisement for the feature. A user
// who understands but isn't interested can dismiss it (the cross next to the
// gate icon): the choice is stored in local storage — namespaced by account
// id, like the other per-user preferences — and gated capture actions are
// then hidden from the console entirely. A usable capture (entitled and
// enabled plugin) bypasses the dismissal: the actions show and work normally.
const CAPTURE_AD_DISMISSED_KEY = "console/capture:dismissed";

/**
 * Per-account "don't show the capture advertisement again" preference, owned
 * by the component listing the actions (the action rows emit `dismiss` up to
 * it). Pass the app-wide `profile` injection from a `setup` function. The
 * account id is watched rather than read once: in the dashboard window the
 * profile is still empty when the console mounts and resolves later.
 */
export const useCaptureAdDismissed = (
  profile: Ref<UseProfile>
): {
  captureAdDismissed: Ref<boolean>;
  dismissCaptureAd: () => void;
  release: () => void;
} => {
  const dismissed = ref(false);
  const stopWatchAccount = watch(
    () => profile.value.user.accountId,
    (accountId) => {
      if (accountId)
        dismissed.value = useBrowserStore(accountId).get(
          CAPTURE_AD_DISMISSED_KEY,
          false
        );
    },
    { immediate: true }
  );
  const dismiss = () => {
    dismissed.value = true;
    useBrowserStore(profile.value.user.accountId).set(
      CAPTURE_AD_DISMISSED_KEY,
      true
    );
  };
  const release = () => {
    stopWatchAccount();
  };
  return {
    captureAdDismissed: dismissed,
    dismissCaptureAd: dismiss,
    release,
  };
};

/**
 * Whether a capture action must be hidden from the console: it is gated (the
 * user can't perform it yet) and the user dismissed the advertisement.
 */
export const isCaptureActionHidden = (
  dismissed: boolean,
  action: ConsoleAction,
  availability: CaptureAvailability
): boolean => dismissed && captureGate(action, availability) !== null;
